import { Transaction, captureError, createTransaction } from "client/utils/apm";
import { filterNullable } from "client/utils/filter";
import resolveObject from "client/utils/resolveObject";
import { getPLPContentPageKey, getTabsKey } from "common/utils/deliveryKey";
import {
  AmpHeroBannerConfig,
  AmpInGridBannerConfig,
  AmpModalConfig,
  AmpPLPContentPage,
  AmpQuickFilterCarousel,
  AmpTabs,
} from "types/amplience";
import { ActiveSlots, Brand, RelevantContent, RelevantSlots } from "types/common";

interface AmplienceErrBody {
  type: string;
  message: string;
  data: Record<string, unknown>;
}

class AmplienceError extends Error {
  type: string;
  data: Record<string, unknown>;

  constructor(error: AmplienceErrBody) {
    super(error.message);
    this.type = error.type;
    this.data = error.data;
  }
}

const getContent = async <
  T extends
    | AmpHeroBannerConfig
    | AmpQuickFilterCarousel
    | AmpInGridBannerConfig
    | AmpModalConfig
    | AmpTabs
    | AmpPLPContentPage,
>(
  url: string,
  { transaction, ignoreError }: { transaction?: Transaction; ignoreError?: boolean },
) => {
  const span = transaction?.startSpan(`GET ${url}`, "external.http");
  try {
    const res = await fetch(url, { redirect: "follow" });
    const body = await res.json();

    if (!res.ok) throw new AmplienceError(body);

    return body as { content: T };
  } catch (error) {
    if (!ignoreError) {
      captureError(error);
    }
    return null;
  } finally {
    span?.end();
  }
};

const mapSlots = <T extends AmpHeroBannerConfig | AmpQuickFilterCarousel | AmpInGridBannerConfig | AmpModalConfig>(
  slots: RelevantContent[] | undefined,
  transaction?: Transaction,
) =>
  slots &&
  Promise.all(
    slots.map(async ({ position, contentURL }) => {
      const res = await getContent<T>(contentURL, { transaction });
      if (!res) return;
      return { position, content: res.content };
    }),
  );

export const getActiveSlots = async (relSlots: NonNullable<RelevantSlots[string]>): Promise<ActiveSlots> => {
  const transaction = createTransaction("Get Active PLP Content");

  const heroBanners = await resolveObject({
    heroBanner: mapSlots<AmpHeroBannerConfig>(relSlots.heroBanner, transaction),
    quickFilters: mapSlots<AmpQuickFilterCarousel>(relSlots.quickFilter, transaction),
    inGridBanner: mapSlots<AmpInGridBannerConfig>(relSlots.inGridBanner, transaction),
    modal: mapSlots<AmpModalConfig>(relSlots.modal, transaction),
  });

  transaction?.end();
  return {
    heroBanner: filterNullable(heroBanners.heroBanner),
    quickFilter: filterNullable(heroBanners.quickFilters),
    inGridBanner: filterNullable(heroBanners.inGridBanner),
    modal: filterNullable(heroBanners.modal),
  };
};

export const getTabs = async (brand: Brand, path: string): Promise<AmpTabs | undefined> => {
  const transaction = createTransaction("Get Tabs Content");

  const basePath = window.__browse_content_initial_state__.baseCDNPath;
  if (!basePath) {
    transaction?.end();
    return;
  }

  const url = new URL(`/content/key/${getTabsKey(brand, path)}`, basePath);

  url.searchParams.set("depth", "all");
  url.searchParams.set("format", "inlined");

  const res = await getContent<AmpTabs>(url.toString(), { transaction, ignoreError: true });

  transaction?.end();
  return res?.content;
};

export const getPLPContentPage = async (brand: Brand, path: string): Promise<AmpPLPContentPage | undefined> => {
  const transaction = createTransaction("Get PLP Content");
  const basePath = window.__browse_content_initial_state__.baseCDNPath;
  if (!basePath) {
    transaction?.end();
    return;
  }

  const fasciaURL = new URL(`/content/key/${getPLPContentPageKey(brand, path)}`, basePath);
  const sharedURL = new URL(`/content/key/${getPLPContentPageKey("shared", path)}`, basePath);

  const [fasciaPage, sharedPage] = await Promise.all(
    [fasciaURL, sharedURL].map((url) => {
      url.searchParams.set("depth", "all");
      url.searchParams.set("format", "inlined");
      return getContent<AmpPLPContentPage>(url.toString(), { transaction, ignoreError: true });
    }),
  );

  const page = fasciaPage || sharedPage;
  return page?.content;
};
